import { MsAreaFilter } from "@/blocks/marketsuite/filters/area/component";
import { DefaultFilters } from "@/components/admin/fields/default-filters/default-filters";
import { FormElement, MsFilterSet } from "@/payload-types";
import type { AnyFieldApi } from "@tanstack/react-form";
import type { ElementType } from "react";
import { Relationship } from "../utils/theme-configuration-class";
import { filterBlockWidthClass } from "@/lib/types/form/field-width";
import { FilterOption, FilterRange, FilterValue, PropertyFilterData, SelectedFilters } from "@/lib/types/marketsuite/datatypes/filters";
import { FILTER_TYPE_DEFINITIONS, FilterDefinition } from "../marketsuite/filters/registry";
import { MsRangeFilter } from "@/blocks/marketsuite/filters/range/component";
import { MsSelectFilter } from "@/blocks/marketsuite/filters/select/component";
import { MsListingTypeFilter } from "@/blocks/marketsuite/filters/listing-type/component";
import { MsTextFilter } from "@/blocks/marketsuite/filters/text/component";

export type { FilterValue, SelectedFilters } from "@/lib/types/marketsuite/datatypes/filters";

export type FilterBlockSlug = FilterDefinition["slug"] | "listing-type";

// Range/select kinds each render a single shared component; "area" and "listing-type"
// have bespoke components. Each component injects its own blockType into onValueChange,
// so the loop below just hands every component the same uniform props.
const FILTER_COMPONENTS: Record<string, React.FC<any>> = {
	area: MsAreaFilter,
	"listing-type": MsListingTypeFilter,
	range: MsRangeFilter,
	select: MsSelectFilter,
	text: MsTextFilter,
};

function getFilterComponent(blockType: string): React.FC<FilterBlockProps> | undefined {
	if (blockType === "area" || blockType === "listing-type") return FILTER_COMPONENTS[blockType];

	const kind = FILTER_TYPE_DEFINITIONS.find((definition) => definition.slug === blockType)?.kind;
	return kind ? FILTER_COMPONENTS[kind] : undefined;
}

function getFilterDataKey(blockType: string) {
	const definition = FILTER_TYPE_DEFINITIONS.find((definition) => definition.slug === blockType);

	return definition?.kind === "text" ? undefined : definition?.dataKey;
}

export interface FilterBlockProps {
	field: AnyFieldApi;
	selectedFilters: SelectedFilters;
	onValueChange?: (value: FilterValue, blockType: string) => void;
	formVariant?: Relationship<FormElement>;
	filterData?: FilterOption[] | string[] | FilterRange;
	content: any;
}

export interface RenderFiltersProps extends Omit<FilterBlockProps, "field" | "filterData" | "content"> {
	filterset: MsFilterSet;
	defaultFilters?: DefaultFilters;
	filterData?: PropertyFilterData;
	form: {
		Field: ElementType;
	};
}

export const RenderFilters: React.FC<RenderFiltersProps> = ({ filterset, defaultFilters, form, selectedFilters, filterData, formVariant, onValueChange }) => (
	<div className="flex w-full flex-wrap gap-x-(--form-gap-x) gap-y-(--form-gap-y)">
		{filterset.filters?.map((block, index) => {
			const { blockType, id } = block;

			if (!blockType) return null;
			if (defaultFilters?.hidden?.includes(blockType)) return null;

			const Block = getFilterComponent(blockType);
			if (!Block) return null;

			const filterDataKey = getFilterDataKey(blockType);

			return (
				<div key={id ?? `${blockType}-${index}`} className={filterBlockWidthClass(block.appearance?.width ?? undefined)}>
					<form.Field name={blockType}>
						{(field: AnyFieldApi) => (
							<Block
								{...block}
								field={field}
								selectedFilters={selectedFilters}
								filterData={filterDataKey ? filterData?.[filterDataKey] : undefined}
								onValueChange={onValueChange}
								formVariant={formVariant}
							/>
						)}
					</form.Field>
				</div>
			);
		})}
	</div>
);
